import { RemoteInterviewPracticeRepo } from '@repository/interviewPractices/InterviewPracticesRepo';
import { RemoteTtsRepo } from '@repository/tts/TtsRepo';
import AccTimer, { ACCTimerOption } from './AccTimer';
import RecorderService from './RecordService';
import InterviewPracticeStepInfo from './InterviewPracticeStepInfo';
import { createStore } from '../utils';
import { PracticeQuestionTextRs } from '@domain/rs/pass/interview/practice/PracticeQuestionTextRs';
import { InterviewPracticeQuestionPolicyRs } from '@domain/rs/pass/interview/practice/InterviewPracticeQuestionPolicyRs';
import {
  QuestionAnswerResultRs,
  QuestionHelpContentTypeCode,
} from '@domain/rs/pass/interview/practice/QuestionAnswerResultRs';
import { WebcamStatus } from '@domain/constant/webcamTypes';
import WebcamUtil from '@utils/WebcamUtil';

const MAX_TEST_TIME = 5;

//질문 관련 class
class PracticeQuestionInfo {
  constructor(readonly practiceQuestions: PracticeQuestionTextRs[], readonly currentPracticeIndex: number) {}

  get questionCount() {
    return this.practiceQuestions.length;
  }
}

interface InterviewPracticeType {
  timer: AccTimer; // 전역에서 사용하는 타이머 객체
  recorderService: RecorderService; //녹화 관련 객체
  practiceQuestionInfo: PracticeQuestionInfo; // 연습 질문들이 담겨져 있는 객체
  practiceStepInfo: InterviewPracticeStepInfo;
  sn: number | null;
  micScale: number; // 마이크 입력 레벨
  lastReadVideo: Blob | null; // 마지막으로 서버에서 불러온 동영상 파일
  policies: InterviewPracticeQuestionPolicyRs; // 연습 면접 정책
  restartedQuestionSnList: number[]; // 다시하기를 누른 경험이 있는 질문 SN의 리스트, 이 리스트에 포함된 질문은 다시하기 버튼이 비활성화 됨
  audio: HTMLAudioElement | null; // TTS를 재생하기 위한 오디오 객체
  pauseTTSAudio: () => void;
  questionDetails: Map<number, QuestionAnswerResultRs>; // 결과화면에서 쓸 질문 상세(의도) 데이터, 로드 되는대로 저장합니다.
  allAnswersSubmitted: boolean; // 동영상 제출전에 Result 페이지의 데이터를 호출하는 것을 막기 위함. 추후 순차적으로 request가 진행되도록 하면 제거해도 되는 flag 변수 입니다.
  selectedQuestionSn: number; // 선택한 질문의 sn
  questionDetailLoading: boolean; // 질문 상세(의도) 데이터 로딩중인지 여부
  anyError: boolean; // 에러가 발생했는지 여부

  initialize: (sn: number) => Promise<void>;
  readPracticeInfo: (sn: number) => Promise<void>;
  readQuestionIntention: (practiceSn: number) => Promise<void>;
  getPracticeVideo: (sn: number) => Promise<void>;
  readQuestionPolicy: () => Promise<void>;
  getTts: (text: string) => Promise<HTMLAudioElement>;
  playForceTTSAudio: (text: string) => Promise<void>;
  startThinking: () => Promise<void>;
  stopThinking: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  saveRecord: (practiceSn: number) => Promise<void>;
  startTest: () => Promise<void>;
  getIsRestarted: (questionSn: number) => boolean;
  updateSelectedQuestionSnByIndex: (index: number) => Promise<void>;
  startTimer: (options: Omit<ACCTimerOption, 'endCallback'>) => Promise<void>;
}

const useInterviewPracticeStore = createStore<InterviewPracticeType>((set, get) => ({
  timer: new AccTimer(),
  recorderService: new RecorderService(() => {
    const { pauseTTSAudio } = get();
    pauseTTSAudio();
  }),
  practiceQuestionInfo: new PracticeQuestionInfo([], 0),
  practiceStepInfo: new InterviewPracticeStepInfo(),
  sn: null,
  micScale: 0,
  lastReadVideo: null,
  policies: {
    interviewPracticeMinQuestionSize: 1,
    interviewPracticeMaxQuestionSize: 10,
    minAnswerReadyTimeSec: 1,
    maxAnswerReadyTimeSec: 30,
    minAnswerTimeSec: 20,
    maxAnswerTimeSec: 90,
    maxRepeatableAnswerTimes: 2,
  },
  restartedQuestionSnList: [],
  audio: null,
  pauseTTSAudio: () => {
    const { audio } = get();
    if (audio && !audio?.paused) audio.pause();
  },
  questionDetails: new Map<number, QuestionAnswerResultRs>(),
  allAnswersSubmitted: true,
  selectedQuestionSn: 0,
  questionDetailLoading: false,
  anyError: false,

  initialize: async (sn: number) => {
    try {
      const { readPracticeInfo, readQuestionPolicy, recorderService } = get();
      const promises = [await readPracticeInfo(sn), await readQuestionPolicy()];
      if (recorderService.status === WebcamStatus.UNINITIALIZED) {
        promises.push(await recorderService.initialize());
      }
      await Promise.all(promises);

      if (recorderService.recorder) {
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(recorderService.recorder.stream);
        const javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(javascriptNode);
        javascriptNode.connect(audioContext.destination);
        javascriptNode.onaudioprocess = () => {
          const array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          set({ micScale: Math.floor(array.reduce((sum, current) => sum + current, 0) / array.length) });
        };
      }
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  readPracticeInfo: async (sn: number) => {
    try {
      const {
        data: { practiceQuestions, runningQuestionSn },
      } = await new RemoteInterviewPracticeRepo().readInterviewPracticeQuestions(sn);
      // 서버에서 받은 현재 진행중인 질문의 SN을 기반으로, findIndex 수행
      const currentIndex = practiceQuestions.findIndex((v) => v.practiceQuestionSn === runningQuestionSn);
      // 제출한 동영상이 하나도 없으면 null이 반환되기에, currentIndex === -1 이면 0을 반환
      set({
        practiceQuestionInfo: new PracticeQuestionInfo(practiceQuestions, currentIndex === -1 ? 0 : currentIndex),
      });
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },

  readQuestionIntention: async (practiceSn: number) => {
    try {
      const { questionDetails } = get();
      const { data } = await new RemoteInterviewPracticeRepo().readQuestionAnswerResult(practiceSn);
      if (data.helpContents === null) {
        // helpContents가 null이면 기본 값으로 채워줍니다.
        data.helpContents = {
          [QuestionHelpContentTypeCode.ANSWER_GUIDE]: [],
          [QuestionHelpContentTypeCode.BAD_ANSWER]: [],
          [QuestionHelpContentTypeCode.CAUTION]: [],
          [QuestionHelpContentTypeCode.GOOD_ANSWER]: [],
          [QuestionHelpContentTypeCode.QUESTION_PURPOSE]: [],
          [QuestionHelpContentTypeCode.QUESTION_PURPOSE_LINK]: [],
          [QuestionHelpContentTypeCode.SIMILAR_QUESTION_ANSWER]: [],
        };
      }
      set({ questionDetails: questionDetails.set(practiceSn, data) });
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  getPracticeVideo: async (sn: number) => {
    try {
      const { data } = await new RemoteInterviewPracticeRepo().readPracticeVideo(sn);
      set({ lastReadVideo: data });
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  readQuestionPolicy: async () => {
    try {
      const { data } = await new RemoteInterviewPracticeRepo().readQuestionPolicy();
      set({ policies: data });
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  getTts: async (text: string) => {
    const res = await new RemoteTtsRepo().getTts(text);
    if (res.status === 200) {
      const data = res.data as unknown as Blob | MediaSource;
      return new Audio(URL.createObjectURL(data));
    }
    return new Audio();
  },
  playForceTTSAudio: async (text: string) => {
    try {
      const { pauseTTSAudio, getTts } = get();
      pauseTTSAudio();
      const newAudio = await getTts(text);
      set({ audio: newAudio });
      newAudio.play();
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  startThinking: async () => {
    const { startTimer, policies } = get();
    await startTimer({ maxTimeSec: policies.maxAnswerReadyTimeSec });
  },
  stopThinking: () => {
    const { timer } = get();
    timer.destroy();
  },
  startRecording: async () => {
    const { recorderService, startTimer, policies } = get();
    await recorderService.startRecord();
    await startTimer({ maxTimeSec: policies.maxAnswerTimeSec });
    await recorderService.stopRecord();
  },
  stopRecording: async () => {
    const { recorderService, timer } = get();
    await recorderService.stopRecord();
    timer.destroy();
  },
  saveRecord: async (practiceSn: number) => {
    const { recorderService } = get();
    if (recorderService.recorder) {
      const blob = recorderService.lastRecordingData;
      // 저장할 데이터가 없으면 저장하지 않음
      // 하지만 저장할 데이터가 없으면 오류를 발생해야합니다. (제대로 녹화가 안된 경우입니다)
      if (blob !== null) {
        try {
          // saveRecord 중에는 lock을 걸어줍니다.
          set({ allAnswersSubmitted: false });
          //웹캠 유틸을 통해 video의 duration을 가져옵니다.
          const duration = await WebcamUtil.getBlobVideoLength(blob);
          // 재생시간은 반올림
          await new RemoteInterviewPracticeRepo().savePractice(practiceSn, Math.round(duration), blob);
          // request가 끝나기에 lock 해제
          set({ allAnswersSubmitted: true });
        } catch (e: any) {
          console.error(e);
          set({ anyError: true });
        }
      } else {
        set({ anyError: true });
      }
    }
  },
  startTest: async () => {
    const { recorderService, startTimer } = get();
    await recorderService.startRecord();
    await startTimer({ maxTimeSec: MAX_TEST_TIME });
    await recorderService.stopRecord();
  },
  getIsRestarted: (questionSn: number) => {
    const { restartedQuestionSnList } = get();
    return restartedQuestionSnList.indexOf(questionSn) > -1;
  },
  updateSelectedQuestionSnByIndex: async (index: number) => {
    try {
      const { practiceQuestionInfo, readQuestionIntention, getPracticeVideo } = get();
      const selectedQuestionSn = practiceQuestionInfo.practiceQuestions[index].practiceQuestionSn;
      set({ selectedQuestionSn, questionDetailLoading: true });
      const promises = [readQuestionIntention(selectedQuestionSn), getPracticeVideo(selectedQuestionSn)];
      await Promise.all(promises);
      set({ questionDetailLoading: false });
    } catch (e: any) {
      console.error(e);
      set({ anyError: true });
    }
  },
  startTimer: async (options: Omit<ACCTimerOption, 'endCallback'>) => {
    return new Promise<void>((resolve) => {
      try {
        const { timer } = get();
        timer.initialize({
          ...options,
          endCallback: () => {
            timer.destroy();
            resolve();
          },
        });
        timer.start();
      } catch (e: any) {
        console.error(e);
        set({ anyError: true });
      }
    });
  },
}));

export default useInterviewPracticeStore;
